import { AlertCircle, CheckCircle2, RefreshCw, X, XCircle } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Spinner } from '@/components/ui/spinner';
import { useClientSync } from '@/context/ClientSyncProvider';

export function ClientSyncProgress() {
  const { syncState, startSync, cancelSync, dismissStatus } = useClientSync();

  if (!syncState || syncState.status === 'idle') return null;

  if (syncState.status === 'PENDING') {
    return (
      <Alert>
        <Spinner size={16} className="text-inherit" />
        <AlertTitle>Preparando sincronização...</AlertTitle>
        <AlertDescription>
          Aguarde enquanto preparamos a sincronização de clientes.
        </AlertDescription>
      </Alert>
    );
  }

  if (syncState.status === 'RUNNING') {
    const pct = syncState.pct ?? 0;
    const total = syncState.total_processos ?? 0;
    const done = syncState.processos_sincronizados ?? 0;
    const created = syncState.clientes_criados ?? 0;

    return (
      <Alert>
        <RefreshCw className="h-4 w-4 animate-spin" />
        <AlertTitle className="flex items-center justify-between">
          <span>Sincronizando clientes de processos</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => cancelSync()}
            className="h-6 w-6 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </AlertTitle>
        <AlertDescription className="space-y-2">
          <Progress value={pct} className="h-2" />
          <p className="text-sm text-muted-foreground">
            {done} de {total} processos sincronizados
            {created > 0 && ` · ${created} clientes importados`}
          </p>
        </AlertDescription>
      </Alert>
    );
  }

  if (syncState.status === 'COMPLETED') {
    const created = syncState.clientes_criados ?? 0;
    const linked = syncState.clientes_vinculados ?? 0;
    const erros = syncState.processos_com_erro ?? 0;
    const hasErrors = erros > 0;

    return (
      <Alert variant={hasErrors ? 'warning' : 'success'}>
        {hasErrors
          ? <AlertCircle className="h-4 w-4" />
          : <CheckCircle2 className="h-4 w-4" />
        }
        <AlertTitle className="flex items-center justify-between">
          <span>Sincronização concluída</span>
          <Button variant="ghost" size="sm" onClick={dismissStatus} className="h-6 w-6 p-0">
            <X className="h-4 w-4" />
          </Button>
        </AlertTitle>
        <AlertDescription>
          {created > 0 || linked > 0
            ? `${created} clientes importados, ${linked} vinculados a processos.`
            : 'Nenhum novo cliente encontrado.'}
          {hasErrors && (
            <span className="block mt-1">
              {erros} processos não puderam ser consultados (indisponibilidade temporária do tribunal).
            </span>
          )}
        </AlertDescription>
      </Alert>
    );
  }

  if (syncState.status === 'FAILED') {
    return (
      <Alert variant="destructive">
        <XCircle className="h-4 w-4" />
        <AlertTitle className="flex items-center justify-between">
          <span>Sincronização interrompida</span>
          <Button variant="ghost" size="sm" onClick={dismissStatus} className="h-6 w-6 p-0 text-destructive-foreground">
            <X className="h-4 w-4" />
          </Button>
        </AlertTitle>
        <AlertDescription className="space-y-2">
          <p>Serviço de consulta processual indisponível. Tentaremos novamente automaticamente.</p>
          <Button variant="outline" size="sm" onClick={() => startSync()}>
            <RefreshCw className="mr-1.5 h-3 w-3" />
            Tentar novamente
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  if (syncState.status === 'CANCELLED') {
    return (
      <Alert>
        <XCircle className="h-4 w-4 text-muted-foreground" />
        <AlertTitle>Sincronização cancelada pelo usuário.</AlertTitle>
      </Alert>
    );
  }

  return null;
}
